/**
 * The ONE place that answers "which deployment environments can this build
 * actually reach?" — so the publish flow can stop telling an appliance
 * operator about places their application cannot go.
 *
 * ── What the appliance actually has (measured 2026-08-18, cogo-platform) ──
 *
 * The appliance runs its own publication control plane,
 * `gridone-appliance-control`, and that control plane does not merely OMIT a
 * production target — it REFUSES one. Every candidate, verification record and
 * execution receipt it admits must carry exactly:
 *
 *     authorityScope            === "appliance-local"
 *     applianceEligible         === true
 *     productionEligible        === false
 *     externalPromotionRequired === true
 *
 * Anything else is rejected — `publication-routes.ts` throws
 * `appliance_publication_verification_failed` (422),
 * `publication-authority.ts` throws `verification_result_contract_invalid`,
 * and a receipt whose `productionEligible !== false` is dropped by
 * `parseReceiptEvidence`. The appliance's own publishing agent then writes
 * `productionEligible: false as const` unconditionally
 * (`cogo-dgx-publishing-agent/src/receipts.ts`), so on that box the field can
 * never hold anything else.
 *
 * Corroborating, from the same read: the appliance's `/api/platform` is
 * `cogo-platform-api`, which serves NO `/api/publication-target-profiles` at
 * all; its publication surface is `publication-staging` →
 * `publication-activation` → `publications/:id/status`, with lifecycle
 * `not-staged | untracked | staged | active | superseded`, all against the one
 * on-box app host (`cogo-app-host`, edge-routed at `/apps/:slug`). Staging and
 * "active" there are two STATES OF ONE HOST, not two sites.
 *
 * ── Why this is a list and not a flag ──
 *
 * `publication-release-panel.tsx` hard-coded `["staging", "production"]` at a
 * single literal, which is a cloud-shaped duality: two hosted sites you pick
 * between. Deriving the same array here means the panel keeps rendering a
 * CHOICE where a choice exists and renders a STATEMENT where only one target
 * does — without any surface re-parsing `VITE_STUDIO_DISTRIBUTION` for itself.
 * It reads {@link isApplianceDistribution}, the single distribution predicate,
 * and nothing else — the same rule `appliance-commerce-surface.ts` follows.
 *
 * ── What this deliberately does NOT change ──
 *
 * The wire vocabulary. `APPLIANCE_PUBLICATION_ENVIRONMENTS` is a strict SUBSET
 * of `CLOUD_PUBLICATION_ENVIRONMENTS` and shares its first element, so the
 * environment an appliance operator sends is exactly the one a cloud operator
 * who touched nothing would send. This changes what the operator is told and
 * offered — never what the publication pipeline produces or is asked for.
 */
import { isApplianceDistribution } from "./studio-presentation-profile";

/** The environment vocabulary the publication API already speaks. */
export type PublicationEnvironment = "staging" | "production";

/**
 * Cloud: two hosted sites, and picking between them is a real choice.
 *
 * Byte-identical to the literal `publication-release-panel.tsx` carried before
 * this module existed, including order — element 0 seeds the panel's
 * `environment` state.
 */
export const CLOUD_PUBLICATION_ENVIRONMENTS: readonly PublicationEnvironment[] =
  Object.freeze(["staging", "production"] as const);

/**
 * Appliance: one target, the box itself.
 *
 * `production` is absent because `gridone-appliance-control` refuses it
 * (`productionEligible !== false` → rejected), not because it is merely
 * unreachable. `externalPromotionRequired: true` in that same record says
 * promotion beyond the appliance is performed OUTSIDE the appliance, by
 * something this Studio build is not.
 */
export const APPLIANCE_PUBLICATION_ENVIRONMENTS: readonly PublicationEnvironment[] =
  Object.freeze(["staging"] as const);

/**
 * The environments this build can actually deploy to.
 *
 * A single element means there is no choice to offer — the caller should state
 * the target rather than ask for one.
 */
export function publicationEnvironmentsForDistribution(
  env?: Partial<Pick<ImportMetaEnv, "VITE_STUDIO_DISTRIBUTION">>,
): readonly PublicationEnvironment[] {
  return isApplianceDistribution(env)
    ? APPLIANCE_PUBLICATION_ENVIRONMENTS
    : CLOUD_PUBLICATION_ENVIRONMENTS;
}
